'use client';

import { useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { Dancing_Script } from 'next/font/google';
import { MapPin, Clock, Award, Zap, X } from 'lucide-react';
import { PublicSection } from '../../../../components/public/PublicSection';
import { PROJECT_NAME } from '@shared/constants';
import { cn } from '@shared/utils';

const dancingScript = Dancing_Script({
  subsets: ['latin'],
  weight: ['400', '700'],
});

/* ------------------------------------------------------------------ */
/*  Feature data                                                       */
/* ------------------------------------------------------------------ */

interface Feature {
  icon: typeof MapPin;
  title: string;
  subtitle: string;
  description: string;
  /** Path to cover image in /public */
  coverImage: string;
  /** Tailwind bg + text classes for the icon badge */
  iconBg: string;
}

const FEATURES: Feature[] = [
  {
    icon: MapPin,
    title: 'Access from Anywhere',
    subtitle: 'No Geographic Barriers',
    description:
      "Geography should never be a barrier to quality healthcare. Consult with qualified doctors whether you're in Cape Town, rural KwaZulu-Natal, or anywhere across our beautiful country.",
    coverImage: '/img/access.png',
    iconBg: 'bg-brand-mint text-primary',
  },
  {
    icon: Clock,
    title: 'Minutes, Not Hours',
    subtitle: 'Rapid Consultations',
    description:
      'No more waiting rooms or queuing for hours. Our intelligent triage system matches you with the right doctor quickly, so you spend minutes getting care, not hours waiting for it.',
    coverImage: '/img/minutes.png',
    iconBg: 'bg-primary text-primary-foreground',
  },
  {
    icon: Award,
    title: 'Quality Care',
    subtitle: 'HPCSA Registered',
    description: `Every doctor on ${PROJECT_NAME} is a fulltime employee, fully registered with the Health Professions Council of South Africa (HPCSA), and follows established clinical protocols. You receive the same standard of care you'd expect in a face-to-face consultation.`,
    coverImage: '/img/quality.png',
    iconBg: 'bg-white text-[#FAAE52]',
  },
  {
    icon: Zap,
    title: 'Smart Triage',
    subtitle: 'AI-Powered Matching',
    description:
      'Your time matters. Our intelligent triage system asks the right questions to understand your symptoms, then matches you with the most appropriate doctor — all within minutes, not hours.',
    coverImage: '/img/triage.png',
    iconBg: 'bg-primary text-primary-foreground',
  },
];

/* ------------------------------------------------------------------ */
/*  Layout constants                                                    */
/* ------------------------------------------------------------------ */

/** Resting tilt angle for each book (degrees) — organic "leaning" look */
const BASE_TILTS = [-5, 3, -2.5, 5];

/** Spread distance by distance-from-hovered (px) */
const SPREAD_PX = [0, 48, 68, 84];

/* ------------------------------------------------------------------ */
/*  Transform helpers                                                   */
/* ------------------------------------------------------------------ */

function getBookTransform(index: number, hoveredIndex: number | null): string {
  const tilt = BASE_TILTS[index] ?? 0;

  /* Resting — tilted, no spread */
  if (hoveredIndex === null) {
    return `rotateZ(${tilt}deg)`;
  }

  /* Hovered — lift, straighten, pop forward */
  if (index === hoveredIndex) {
    return 'translateY(-28px) rotateZ(0deg) scale(1.1)';
  }

  /* Others — spread away from the hovered book */
  const diff = index - hoveredIndex;
  const direction = diff > 0 ? 1 : -1;
  const distance = Math.abs(diff);
  const spread =
    direction * (SPREAD_PX[distance] ?? SPREAD_PX[SPREAD_PX.length - 1]!);
  const extraTilt = direction * 2;

  return `translateX(${spread}px) rotateZ(${tilt + extraTilt}deg) scale(0.96)`;
}

function getBookZIndex(index: number, hoveredIndex: number | null): number {
  if (hoveredIndex === null) return index + 1;
  if (index === hoveredIndex) return 20;
  return index + 1;
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

/**
 * @group Landing
 * @summary Top-down doctor's desk with books, hover-to-browse, and click-to-open modal
 */
export function FeaturesSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);

  const handleBookClick = useCallback(
    (idx: number) => {
      if (selectedIndex !== null) return;
      setSelectedIndex(idx);
    },
    [selectedIndex],
  );

  const handleClose = useCallback(() => {
    setCoverOpen(false);
    setTimeout(() => {
      setShowModal(false);
      setTimeout(() => setSelectedIndex(null), 420);
    }, 480);
  }, []);

  /* orchestrate enter animation after modal mounts */
  useEffect(() => {
    if (selectedIndex === null) return;
    const t1 = setTimeout(() => setShowModal(true), 30);
    const t2 = setTimeout(() => setCoverOpen(true), 520);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [selectedIndex]);

  /* lock body scroll while modal is open */
  useEffect(() => {
    if (selectedIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedIndex]);

  /* escape-key handler */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedIndex !== null) handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedIndex, handleClose]);

  return (
    <>
      <PublicSection 
        heading={`What Makes ${PROJECT_NAME} Different`} 
        headingColor="hsl(var(--brand-mint))"
        subheadingColor="hsl(var(--brand-mint))"
        separatorColor="hsl(var(--brand-mint))"
        separatorLineColor="hsl(var(--brand-mint) / 0.4)"
        className="bg-brand-teal"
        indented
      >
        <div className="relative max-w-5xl mx-auto">
          {/* Container with decorative images and books */}
          <div className="relative ">
            

            {/* ---- Browsable book row (on top of iPad/stethoscope) ---- */}
            <div
              className="relative flex items-center justify-center px-4 sm:px-8 lg:px-12 py-8 sm:py-12"
              style={{ zIndex: 2 }}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {FEATURES.map((feature, idx) => (
                <ShelfBook
                  key={idx}
                  feature={feature}
                  index={idx}
                  hoveredIndex={hoveredIndex}
                  isSelected={selectedIndex === idx}
                  onHover={setHoveredIndex}
                  onClick={() => handleBookClick(idx)}
                />
              ))}
            </div>
          </div>
        </div>

      </PublicSection>

      {/* ---- Book modal (outside PublicSection to avoid perspective stacking) ---- */}
      {selectedIndex !== null && (
        <BookModal
          feature={FEATURES[selectedIndex]}
          showModal={showModal}
          coverOpen={coverOpen}
          onClose={handleClose}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  ShelfBook                                                          */
/* ------------------------------------------------------------------ */

function ShelfBook({
  feature,
  index,
  hoveredIndex,
  isSelected,
  onHover,
  onClick,
}: {
  feature: Feature;
  index: number;
  hoveredIndex: number | null;
  isSelected: boolean;
  onHover: (idx: number) => void;
  onClick: () => void;
}) {
  const isHovered = hoveredIndex === index;

  return (
    <button
      onMouseEnter={() => onHover(index)}
      onClick={onClick}
      aria-expanded={isSelected}
      className={cn(
        'relative flex-shrink-0 text-left rounded-lg cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
      )}
      style={{
        width: 'clamp(120px, 18vw, 220px)',
        marginLeft: index === 0 ? 0 : 'clamp(-28px, -5.5vw, -70px)',
        transform: getBookTransform(index, hoveredIndex),
        zIndex: getBookZIndex(index, hoveredIndex),
        transformOrigin: 'bottom center',
        transition: 'transform 0.55s cubic-bezier(0.23, 1, 0.32, 1)',
        willChange: isHovered ? 'transform' : undefined,
      }}
    >
      {/* Book cover */}
      <div
        className={cn(
          'relative rounded-lg overflow-hidden',
          'transition-shadow duration-500',
          isHovered
            ? 'shadow-[0_20px_50px_rgba(0,0,0,0.3)]'
            : 'shadow-[0_4px_16px_rgba(0,0,0,0.15)]',
        )}
        style={{ aspectRatio: '3 / 4' }}
      >
        <BookCoverArt feature={feature} />
      </div>

      {/* Shadow on desk surface */}
      <div
        className={cn(
          'absolute -bottom-1 left-[8%] right-[8%] h-2.5 rounded-full blur-sm',
          'bg-black/10 transition-all duration-500',
          isHovered && 'bg-black/25 blur-lg h-3',
        )}
      />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  BookCoverArt  (shared between shelf thumbnail + modal cover)       */
/* ------------------------------------------------------------------ */

function BookCoverArt({
  feature,
  large = false,
}: {
  feature: Feature;
  large?: boolean;
}) {
  return (
    <>
      {/* Cover image */}
      <Image
        src={feature.coverImage}
        alt={feature.title}
        fill
        sizes={large ? '340px' : '220px'}
        className="object-cover"
        quality={85}
        priority
      />

      {/* Bottom gradient for text readability */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />

      {/* Title block at bottom */}
      <div
        className={cn(
          'relative z-10 h-full flex flex-col justify-end',
          large ? 'p-5 sm:p-6' : 'p-2.5 sm:p-3 lg:p-4',
        )}
      >
        <h3
          className={cn(
            dancingScript.className,
            'text-white font-bold leading-tight drop-shadow-md',
            large ? 'text-lg sm:text-xl' : 'text-[11px] sm:text-sm lg:text-base',
          )}
        >
          {feature.title}
        </h3>
        <p
          className={cn(
            dancingScript.className,
            'text-white/70 drop-shadow-sm',
            large ? 'text-sm mt-1' : 'text-[9px] sm:text-xs mt-0.5',
          )}
        >
          {feature.subtitle}
        </p>
      </div>

      {/* Spine shadow (left edge) */}
      <div className="absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/35 to-transparent pointer-events-none" />

      {/* Page edges (right side) */}
      <div className="absolute top-2 right-0 bottom-2 w-[3px] bg-gradient-to-l from-white/25 to-transparent pointer-events-none rounded-r-sm" />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  BookModal – full-screen overlay with animated book-open            */
/* ------------------------------------------------------------------ */

function BookModal({
  feature,
  showModal,
  coverOpen,
  onClose,
}: {
  feature: Feature;
  showModal: boolean;
  coverOpen: boolean;
  onClose: () => void;
}) {
  const bookW = 'clamp(225px, 36vw, 510px)';
  const bookH = 'clamp(300px, 48vw, 675px)';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={feature.title}
    >
      {/* ---- backdrop ---- */}
      <div
        className={cn(
          'absolute inset-0 bg-black/60 backdrop-blur-sm',
          'transition-opacity duration-500',
          showModal ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* ---- close button ---- */}
      <button
        onClick={onClose}
        className={cn(
          'absolute top-4 right-4 sm:top-6 sm:right-6 z-20 p-2.5 rounded-full',
          'bg-white/10 hover:bg-white/20 text-white transition-all duration-300',
          showModal ? 'opacity-100 scale-100' : 'opacity-0 scale-75',
        )}
        aria-label="Close"
      >
        <X className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>

      {/* ---- open book container ---- */}
      <div
        className={cn(
          'relative z-10',
          showModal
            ? 'scale-100 opacity-100 translate-y-0'
            : 'scale-[0.35] opacity-0 translate-y-16',
        )}
        style={{
          width: coverOpen ? `calc(2 * ${bookW})` : bookW,
          perspective: '2000px',
          transition: [
            'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
            'opacity 0.5s ease',
            'width 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
          ].join(', '),
        }}
      >
        {/* Inner wrapper — always 2× book width; shifts left when closed */}
        <div
          style={{
            position: 'relative',
            width: `calc(2 * ${bookW})`,
            height: bookH,
            transform: coverOpen
              ? 'translateX(0)'
              : `translateX(calc(-1 * ${bookW}))`,
            transition: 'transform 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* ===== Pages (right half of the open book) ===== */}
          <div
            className="absolute top-0 h-full rounded-r-lg overflow-hidden"
            style={{ left: bookW, width: bookW }}
          >
            {/* Paper background */}
            <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-white to-stone-50" />
            {/* Binding crease */}
            <div className="absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-stone-300/50 to-transparent" />
            {/* Top accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/40 via-primary/10 to-transparent" />

            <div className="relative h-full p-4 sm:p-5 md:p-8 pl-5 sm:pl-6 md:pl-9 flex flex-col justify-center overflow-y-auto ">
              <p
                className="text-base sm:text-lg md:text-xl text-foreground/80 leading-relaxed font-serif"
              >
                {feature.description}
              </p>

              {/* Page footer decoration */}
              <div className="mt-4 sm:mt-6 pt-3 border-t border-stone-200/60 text-center">
                <p className="text-[10px] sm:text-xs text-muted-foreground/40 tracking-widest uppercase">
                  &mdash; {feature.title} &mdash;
                </p>
              </div>
            </div>
          </div>

          {/* ===== Cover — starts on right half, flips open to left half ===== */}
          <div
            className="absolute top-0 h-full"
            style={{
              left: bookW,
              width: bookW,
              transformOrigin: 'left center',
              transform: coverOpen ? 'rotateY(-180deg)' : 'rotateY(0deg)',
              transition: 'transform 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
              transformStyle: 'preserve-3d',
              zIndex: 2,
            }}
          >
            {/* Front face (cover art) */}
            <div
              className="absolute inset-0 rounded-lg overflow-hidden shadow-2xl"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <BookCoverArt feature={feature} large />
            </div>

            {/* Back face (inside of cover) */}
            <div
              className="absolute inset-0 rounded-l-lg overflow-hidden"
              style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              {/* Subtle echo of the cover image */}
              <Image
                src={feature.coverImage}
                alt=""
                fill
                sizes="340px"
                className="object-cover opacity-100"
                quality={85}
              />
              {/* Warm parchment overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50/30 to-amber-100/80" />

              {/* Decorative bookplate */}
              <div className="absolute inset-0 flex items-center justify-center p-6 sm:p-8">
                <div className="w-4/5 max-w-[200px] border-2 border-amber-800/20 rounded-sm p-4 sm:p-5 text-center bg-white">
                  <p className="text-[10px] text-amber-900/30 uppercase tracking-[0.2em] mb-1.5">
                    Ex Libris
                  </p>
                  <p
                    className={cn(
                      dancingScript.className,
                      'text-amber-900/50 text-base sm:text-lg',
                    )}
                  >
                    {feature.title}
                  </p>
                </div>
              </div>

              {/* Spine shadow at the binding edge */}
              <div className="absolute inset-y-0 right-0 w-5 bg-gradient-to-l from-black/20 via-black/5 to-transparent" />
            </div>
          </div>

          {/* Book shadow beneath */}
          <div
            className={cn(
              'absolute -bottom-4 left-[5%] right-[5%] h-5 rounded-full blur-xl transition-all duration-700',
              showModal ? 'bg-black/20' : 'bg-black/0',
            )}
          />
        </div>
      </div>
    </div>
  );
}
